// SOLE file allowed to read import.meta.env (enforced by ESLint no-restricted-syntax).
// Validates env at bootstrap with Zod; throws fast if a required var is missing or malformed.

import { z } from 'zod';

const envSchema = z.object({
  // axios baseURL. Empty in dev so requests are relative and Vite's /api proxy handles them.
  // In prod, set the full origin (e.g. https://api.almanar.ai).
  VITE_API_BASE_URL: z.string().default(''),
  // Social-login client ids. Empty disables that provider's button. The Google
  // value must equal the API's GOOGLE_OAUTH_CLIENT_ID (it's the ID token's
  // audience); the Apple value is the Services ID (= API's APPLE_CLIENT_ID).
  VITE_GOOGLE_CLIENT_ID: z.string().default(''),
  VITE_APPLE_CLIENT_ID: z.string().default(''),
});

// eslint-disable-next-line no-restricted-syntax -- only place allowed to touch import.meta.env
const rawEnv = import.meta.env;
const result = envSchema.safeParse(rawEnv);

if (!result.success) {
  // Fail fast at bootstrap — never let a misconfigured app limp along to its first network call.
  console.error('[global-config] Environment validation failed:', result.error.format());
  throw new Error(
    '[global-config] Environment validation failed at bootstrap. Check .env.local against .env.example.',
  );
}

export const config = Object.freeze({
  apiBaseUrl: result.data.VITE_API_BASE_URL,
  googleClientId: result.data.VITE_GOOGLE_CLIENT_ID,
  appleClientId: result.data.VITE_APPLE_CLIENT_ID,
  isDev: Boolean(rawEnv.DEV),
  isProd: Boolean(rawEnv.PROD),
} as const);

export type AppConfig = typeof config;
